/* eslint-disable no-template-curly-in-string */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { Modal, Tabs } from '@scale/scaleui';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';

export const ChatIFrame = ({ src }: { src: string }) => {
  const config = useChatStoreExtract(state => state.config);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState('600px');

  const extractContent = (regex: RegExp) => {
    const match = src.match(regex);
    return match ? match[1].trim() : '';
  };

  const htmlContent = extractContent(/```html([\s\S]*?)```/);
  const cssContent = extractContent(/```css([\s\S]*?)```/);
  const jsContent = extractContent(/```(?:js|JAVASCRIPT|javascript)([\s\S]*?)```/);

  const templateString = `
    <!DOCTYPE html>
    <html>
      <head>
        <style>${cssContent}</style>
      </head>
      <body>
        ${htmlContent}
        <script type="text/javascript">
          (function() {
            ${jsContent}
          })();
        </script>
      </body>
    </html>
  `;

  const modifiedSrc = config.codeRegex
    ? src.replace(new RegExp(config.codeRegex, 'g'), (match, capturedContent) => {
        return templateString.replace('${htmlContent}', capturedContent.trim());
      })
    : templateString;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('rendered');

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const tabs = useMemo(() => {
    const availableTabs = [
      { id: 'rendered', label: 'Rendered' },
      { id: 'combined', label: 'Combined' },
      ...(htmlContent ? [{ id: 'html', label: 'HTML' }] : []),
      ...(cssContent ? [{ id: 'css', label: 'CSS' }] : []),
      ...(jsContent ? [{ id: 'js', label: 'JavaScript' }] : []),
    ];
    return availableTabs;
  }, [htmlContent, cssContent, jsContent]);

  const updateIframeHeight = () => {
    try {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        const height = iframeRef.current.contentWindow.document.body.scrollHeight;
        setIframeHeight(`${height + 40}px`);
      }
    } catch (error) {
      // If accessing contentWindow fails due to security restrictions, set a default height
      setIframeHeight('450px');
    }
  };

  useEffect(() => {
    window.addEventListener('resize', updateIframeHeight);
    return () => {
      window.removeEventListener('resize', updateIframeHeight);
    };
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'rendered':
        return (
          <iframe
            ref={iframeRef}
            sandbox="allow-scripts"
            className="w-full h-full border-none"
            srcDoc={modifiedSrc}
            style={{ height: iframeHeight }}
            onLoad={updateIframeHeight}
          />
        );
      case 'html':
        return (
          <ChatMarkdown
            className="text-sm"
            markdown={`\`\`\`html\n${String(htmlContent).replace(/\n$/, '')}\n\`\`\``}
            defaultExpandCodeBlocks={true}
          />
        );
      case 'css':
        return (
          <ChatMarkdown
            className="text-sm"
            markdown={`\`\`\`css\n${String(cssContent).replace(/\n$/, '')}\n\`\`\``}
            defaultExpandCodeBlocks={true}
          />
        );
      case 'js':
        return (
          <ChatMarkdown
            className="text-sm"
            markdown={`\`\`\`js\n${String(jsContent).replace(/\n$/, '')}\n\`\`\``}
            defaultExpandCodeBlocks={true}
          />
        );
      case 'combined':
        return (
          <ChatMarkdown
            className="text-sm"
            markdown={`\`\`\`html\n${templateString}\n\`\`\``}
            defaultExpandCodeBlocks={true}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col my-2" onClick={e => e.preventDefault()}>
      <StepCard>
        <StepCard.Header>
          <StepCard.HeaderLeft>
            <StepCard.HeaderColumn>
              <StepCard.Subtitle>
                <div className="flex justify-between">Rendered Code</div>
              </StepCard.Subtitle>
            </StepCard.HeaderColumn>
          </StepCard.HeaderLeft>
          <StepCard.HeaderRight>
            <StepCard.Button
              icon="expand"
              tooltip="Expand"
              onClick={e => {
                e.preventDefault();
                toggleModal();
              }}
            />
          </StepCard.HeaderRight>
        </StepCard.Header>
        <StepCard.Body>
          <div className="relative">
            <iframe
              ref={iframeRef}
              className="w-full border border-neutral-200 rounded"
              srcDoc={modifiedSrc}
              style={{ width: '100%', height: iframeHeight }}
              onLoad={updateIframeHeight}
            />
          </div>
        </StepCard.Body>
      </StepCard>
      <Modal open={isModalOpen} onClose={toggleModal} title="Rendered code (expanded view)">
        <div className="w-screen h-screen flex flex-col">
          <Tabs
            tabs={tabs}
            onChange={id => setActiveTab(id as string)}
            value={activeTab}
            className="mb-4"
          />
          {renderTabContent()}
        </div>
      </Modal>
    </div>
  );
};
